import SearchIcon from '../../../../ui/svg/common/search';
import React, { ChangeEvent, KeyboardEvent, MouseEvent, useCallback, useEffect } from 'react';
import { debounce } from 'lodash';
import DropPanel from './drop-panel';
import { rootActions, useAppSelector } from '../data/store';
import { getSearchData } from '../data/data-service';
import { useDispatch } from 'react-redux';
import { mod } from '../../../../domain/math-provider';
import { CLOSE_HEADER_ACCOUNT_EVENT } from '../../account/ui/main';
import { CLOSE_MINI_CART_EVENT, CLOSE_NOTIFICATIONS_EVENT } from '../../top-minicart/ui/main';
import { getGeneralSearchUrl } from '../domain/search-provider';
import { sendGAEvent } from '../../../../domain/marketing/gtm-provider';

export const CLOSE_SEARCH_D_EVENT = 'CLOSE_SEARCH_D_EVENT';

const closeOtherPopups = () => {
    // close account and mini-cart
    document.dispatchEvent(new CustomEvent(CLOSE_MINI_CART_EVENT));
    document.dispatchEvent(new CustomEvent(CLOSE_HEADER_ACCOUNT_EVENT));
    document.dispatchEvent(new CustomEvent(CLOSE_NOTIFICATIONS_EVENT));
};

const SiteHeaderSearchDesktop = () => {

    const query = useAppSelector(store => store.root.query);
    const selectedItemIndex = useAppSelector(store => store.root.selectedItemIndex);
    const filtered = useAppSelector(store => store.root.filtered);
    const isOpened = useAppSelector(store => store.root.isOpened);

    const dispatch = useDispatch();

    // ------------------ SEARCH -----------------------

    const search = async (inputText: string) => {

        if(inputText.trim() === ''){
            dispatch(
                rootActions.main({
                    filtered: [],
                    query: '',
                    selectedItemIndex: -1,
                })
            );
            return;
        }

        const response = await getSearchData(inputText);

        if(!response || response.statusCode !== 200 || !response.data.isValid || !response.data.relatedData){
            return;
        }

        const responseResults = response.data.relatedData || [];

        if(inputText.length > 1) {
            // q: {query} | rc: {number of results}
            // q: api | rc: 4
            sendGAEvent('AutoCompleteQuery', `q: ${ inputText.toLowerCase() } | rc: ${ responseResults.length }`);
        }

        dispatch(
            rootActions.main({
                filtered: responseResults,
                // query: inputText,
                selectedItemIndex: -1,
            })
        );

        open();
    };

    // debounce is an external dependency, and it can't be replaced with inline function
    // eslint-disable-next-line
    const debounced = useCallback(
        debounce(
            (inputText) => search(inputText),450
        ),
        []
    );

    const handleInput = (evt: ChangeEvent<HTMLInputElement>) => {
        dispatch(
            rootActions.main({
                query: evt.target.value,
            })
        );

        debounced(evt.target.value);
    };

    // ---------------------------------------------------------

    const open = () => {
        dispatch(
            rootActions.main({
                isOpened: true,
            })
        );

        // close account and mini-cart
        closeOtherPopups();
    };

    const close = useCallback(() => {
        dispatch(
            rootActions.main({
                isOpened: false,
            })
        )
    }, [dispatch]);

    const handleClick = () => {

        // close account and mini-cart
        closeOtherPopups();

        if(query.trim() === '') return;
        open();
    };

    /**
     * handle click outside
     */
    useEffect(() => {

        document.addEventListener('click', close);

        return () => {
            document.removeEventListener('click', close);
        };
    }, [close]);

    /**
     * Close search desktop from outside.
     */
    useEffect(() => {
        document.addEventListener(CLOSE_SEARCH_D_EVENT, close);

        return () => {
            document.removeEventListener(CLOSE_SEARCH_D_EVENT, close);
        };
    }, [dispatch, close]);

    const handleKeyUp = (evt: KeyboardEvent) => {

        if(evt.key === 'ArrowDown') {
            evt.preventDefault();
            evt.stopPropagation();

            dispatch(
                rootActions.main({
                    selectedItemIndex: mod(selectedItemIndex + 1, filtered.length),
                })
            )
        }

        if(evt.key === 'ArrowUp') {
            evt.preventDefault();
            evt.stopPropagation();

            dispatch(
                rootActions.main({
                    selectedItemIndex: selectedItemIndex === -1 ? (filtered.length - 1) : mod(selectedItemIndex - 1, filtered.length),
                })
            )
        }

        if(evt.key === 'Enter') {
            evt.preventDefault();
            evt.stopPropagation();

            let redirect = '';

            const selectedItem = filtered[selectedItemIndex];
            if(selectedItem){
                redirect = selectedItem.documentUrlPath;
            }
            else{
                /*
                if(filtered.length > 0){
                    redirect = filtered[0].documentUrlPath;
                }*/
                redirect = getGeneralSearchUrl(query);
            }

            if(!redirect) return;

            close();
            window.location.href = redirect;
        }

        if(evt.key === 'Escape') {
            close();
        }
    };

    const stopPropagation = (evt: MouseEvent) => {
        evt.stopPropagation();
    };

    return (
        <section role="search" className="relative mx-8 flex items-center h-full" onClick={ stopPropagation }>

            <div className="flex items-center">
                <button type="button" className="w-3.5">
                    <SearchIcon width="100%" height="100%" color="#ffffff" title="Search" />
                </button>

                <label htmlFor="header-site-search" className="ml-3">
                    <input
                        onClick={ handleClick }
                        onInput={ handleInput }
                        onKeyUp={ handleKeyUp }
                        value={ query }
                        className="w-36 bg-transparent text-white placeholder-white text-tiny font-medium outline-none"
                        type="text"
                        name="search"
                        id="header-site-search"
                        placeholder="Search"
                        autoComplete="off"
                    />
                    <span className="sr-only">Search</span>
                </label>
            </div>

            { (isOpened && query.trim() !== '') && <DropPanel /> }

        </section>
    )
};

export default SiteHeaderSearchDesktop;